import { useCallback, useEffect, useRef } from 'react';
import { showToast } from '@ideoz/ui';
import { useConversation } from '../context/ConversationContext';
import { shouldStartBuildStream } from '../../prototyper/utils/shouldStartBuildStream';
import { readBuiltTurnIds } from '../../prototyper/utils/builtTurnIds';
import { isBenignMissingSnapshotError, isSupersededRestoreError } from '../../prototyper/utils/snapshotRestoreErrors';

/**
 * Owns the WebContainer build-stream orchestration for the conversation
 * workspace (extracted verbatim from `ConversationModule` in Phase 2, slice 4):
 * the refs + plan helpers + effects that decide *when* to auto-start a prototype
 * build once the assistant's plan has streamed in (deferring around snapshot
 * restore), plus the four prototype action handlers.
 *
 * Reads `facade`/`workbench`/`urlConversationId`/`isWeb` from `useConversation()`
 * — the same object identities the module used, so the moved effect dep arrays
 * are unchanged and behavior is preserved. The few non-context values come in as
 * params. `resetBuildStreamLatch` is the single external write to the "one
 * build-start per llmInteractionId" latch (called by the module's `handleSubmit`).
 */
interface UseConversationPrototypeStreamParams {
  isPrototypeOpen: boolean;
  setIsPrototypeOpen: (open: boolean) => void;
  isReplyStillActive: (
    submittedConversationId: string | null | undefined,
    newConversationId?: string
  ) => boolean;
  currentPrototypeId: number | null;
  hasPrototypeCredits: boolean;
}

export function useConversationPrototypeStream({
  isPrototypeOpen,
  setIsPrototypeOpen,
  isReplyStillActive,
  currentPrototypeId,
  hasPrototypeCredits,
}: UseConversationPrototypeStreamParams) {
  const { facade, workbench, urlConversationId, isWeb } = useConversation();
  const { currentMode } = facade.mode;
  const { isPrototyper } = facade.headers;
  const { currentConversationId, messages, isStreaming, currentProjectId, initiatedFromUncover } =
    facade.state;
  const { fetchPrototyperById } = facade.prototyper;
  const {
    isStreamingPrototype = false,
    onStreamingPrototypeStart,
    onStreamingWorkbenchPrototypePanelVisibilityChange,
    workbenchSnapshotBootstrap,
    getWorkbenchSnapshotIndexEntry,
    workbenchSnapshotIndexLoading,
    onPrototyperWorkbenchRestore,
    resumeInterruptedBuild,
  } = workbench;

  /** WebContainer: one build-stream start per prototyper assistant `llmInteractionId` (follow-up prompts). */
  const lastStreamingBuildLlmInteractionIdRef = useRef<string | undefined>(undefined);
  /** Opened panel while snapshot bootstrap was `pending`; call build-stream once it settles. */
  const pendingDeferredWorkbenchStreamRef = useRef(false);
  /** Plan id held while waiting for snapshot bootstrap to finish. */
  const pendingDeferredPlanIdRef = useRef<string | undefined>(undefined);
  const lastModuleResetConversationIdRef = useRef<string | null | undefined>(undefined);
  const onStreamingWorkbenchPrototypePanelVisibilityChangeRef = useRef(
    onStreamingWorkbenchPrototypePanelVisibilityChange
  );
  useEffect(() => {
    onStreamingWorkbenchPrototypePanelVisibilityChangeRef.current =
      onStreamingWorkbenchPrototypePanelVisibilityChange;
  }, [onStreamingWorkbenchPrototypePanelVisibilityChange]);

  /** The only external write to the build-start latch (module's `handleSubmit` calls this). */
  const resetBuildStreamLatch = useCallback(() => {
    lastStreamingBuildLlmInteractionIdRef.current = undefined;
  }, []);

  /** One local-resume attempt per turn per conversation session (survives the build-stream latch reset). */
  const resumeStartedIdsRef = useRef<Set<string>>(new Set());

  /**
   * A turn was snapshotted at build-start but never verified — the build was interrupted
   * (refresh, lost tab). Finish it locally; only a genuine local build failure falls back to
   * a real LLM fix-stream, and only when the user has credits to spend on it.
   */
  const runResumeInterruptedBuild = useCallback(
    (llmInteractionId: string) => {
      if (resumeStartedIdsRef.current.has(llmInteractionId)) return;
      resumeStartedIdsRef.current.add(llmInteractionId);
      if (!resumeInterruptedBuild) {
        // Wiring not available (e.g. panel not gated open yet) — fall back to today's behavior
        // rather than leaving the turn permanently stuck.
        if (hasPrototypeCredits) {
          onStreamingPrototypeStart?.({ llmInteractionId });
        }
        return;
      }
      void resumeInterruptedBuild(llmInteractionId).then((outcome) => {
        if (outcome !== 'failed') return;
        if (!hasPrototypeCredits) return;
        onStreamingPrototypeStart?.({ llmInteractionId });
      });
    },
    [resumeInterruptedBuild, onStreamingPrototypeStart, hasPrototypeCredits]
  );

  // Streaming workbench: mirror prototype-panel visibility to the parent so it
  // can defer the WebContainer boot until the panel is actually open.
  useEffect(() => {
    if (!isStreamingPrototype || !onStreamingWorkbenchPrototypePanelVisibilityChange) return;
    onStreamingWorkbenchPrototypePanelVisibilityChange(isPrototypeOpen);
  }, [isStreamingPrototype, isPrototypeOpen, onStreamingWorkbenchPrototypePanelVisibilityChange]);

  /**
   * When using WebContainer build-stream, only start after conversation streaming finishes,
   * the assistant plan exists in Redux, and Nest has attached `llmInteractionId` to that turn.
   */
  const isWorkbenchStreamingPlanReady = useCallback(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    const text = lastAssistant?.content?.trim() ?? '';
    if (!text) return false;
    // The scope's one-shot `…/new` auto-prompt greeting streams in as the opening
    // message with no preceding user bubble (see useConversationPageFacade's
    // greeting effect) — the backend can still tag it `mode: 'prototyper'` when
    // continuing a scope that's mid-build, but it's a conversational "welcome
    // back" turn, never an actionable build plan. Without a real user turn behind
    // it, never auto-start a build-stream for it.
    if (!messages.some((m) => m.role === 'user')) return false;
    // `prototypeReady` is the only valid signal — it's set exclusively from the
    // turn's own `prototype_ready` event (live) or its persisted twin (history).
    // There is deliberately no length/header-based fallback: `isPrototyper` is a
    // conversation-level flag that stays true after any past prototype_ready turn,
    // so a long *clarifying* reply on a later, non-building turn must not be
    // mistaken for a build plan.
    return lastAssistant?.prototypeReady === true;
  }, [messages]);

  /**
   * Latest assistant message that qualifies as the WebContainer prototyper plan
   * (same rules as {@link isWorkbenchStreamingPlanReady}).
   */
  const getLatestPrototyperWorkbenchPlanMessage = useCallback(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant) return undefined;
    const text = lastAssistant.content?.trim() ?? '';
    if (!text) return undefined;
    if (!messages.some((m) => m.role === 'user')) return undefined;
    return lastAssistant.prototypeReady === true ? lastAssistant : undefined;
  }, [messages]);

  /**
   * Handle prototyper header - open prototype panel when x-prototyper header is received
   */
  useEffect(() => {
    const wbBootstrap = workbenchSnapshotBootstrap ?? 'none';
    const cannotStartBase = !isPrototyper || !currentConversationId;

    if (cannotStartBase) {
      return;
    }

    const switchToPrototyperModeIfNeeded = (): void => {
      if (currentMode !== 'prototyper' && !initiatedFromUncover) {
        // showToast({
        //   message: 'Switching to Prototyper Mode...',
        //   severity: 'success',
        //   position: 'top-center',
        // });
        facade.mode.handleModeChange('prototyper');
      }
    };

    const openPanelAndStreamingCreate = (planLlmInteractionId: string): void => {
      switchToPrototyperModeIfNeeded();
      setIsPrototypeOpen(true);

      const decision = shouldStartBuildStream({
        planLlmInteractionId,
        builtTurnIds: readBuiltTurnIds(currentConversationId ?? undefined),
        bootstrap: wbBootstrap,
        planMessage: getLatestPrototyperWorkbenchPlanMessage(),
        getSnapshotIndexEntry: getWorkbenchSnapshotIndexEntry,
        snapshotIndexLoading: workbenchSnapshotIndexLoading,
      });

      if (decision === 'skip') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        return;
      }
      if (decision === 'wait') {
        pendingDeferredWorkbenchStreamRef.current = true;
        pendingDeferredPlanIdRef.current = planLlmInteractionId;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        return;
      }
      if (decision === 'resume-build') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        // Latch before the async resume settles — this is the primary re-entry guard against
        // a second effect run (StrictMode, re-render) starting a second resume for this turn.
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        runResumeInterruptedBuild(planLlmInteractionId);
        return;
      }
      pendingDeferredWorkbenchStreamRef.current = false;
      pendingDeferredPlanIdRef.current = undefined;
      onStreamingPrototypeStart?.({ llmInteractionId: planLlmInteractionId });
      lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
    };

    // WebWorkbench streaming: defer until SSE finished, plan ready, and llmInteractionId persisted.
    if (isStreamingPrototype) {
      if (isStreaming) {
        return;
      }
      if (!isWorkbenchStreamingPlanReady()) {
        return;
      }

      const planMsg = getLatestPrototyperWorkbenchPlanMessage();
      const planLlmId = planMsg?.llmInteractionId;
      if (!planLlmId) {
        return;
      }
      if (planLlmId === lastStreamingBuildLlmInteractionIdRef.current) {
        return;
      }
      openPanelAndStreamingCreate(planLlmId);
      return;
    }

    return undefined;
  }, [
    isPrototyper,
    currentConversationId,
    facade.mode,
    currentMode,
    initiatedFromUncover,
    isStreamingPrototype,
    onStreamingPrototypeStart,
    workbenchSnapshotBootstrap,
    isStreaming,
    isWorkbenchStreamingPlanReady,
    getLatestPrototyperWorkbenchPlanMessage,
    messages,
    getWorkbenchSnapshotIndexEntry,
    workbenchSnapshotIndexLoading,
    runResumeInterruptedBuild,
  ]);

  /** If the panel opened while S3 restore was still `pending`, start build-stream once bootstrap settles. */
  useEffect(() => {
    const wb = workbenchSnapshotBootstrap ?? 'none';
    if (!isStreamingPrototype || wb === 'pending') return;
    if (!isPrototypeOpen) return;
    if (isStreaming) return;
    if (!pendingDeferredWorkbenchStreamRef.current) return;

    const planMsg = getLatestPrototyperWorkbenchPlanMessage();
    const planId = pendingDeferredPlanIdRef.current ?? planMsg?.llmInteractionId;
    if (!planId) return;

    const decision = shouldStartBuildStream({
      planLlmInteractionId: planId,
      builtTurnIds: readBuiltTurnIds(currentConversationId ?? undefined),
      bootstrap: wb,
      planMessage: planMsg,
      getSnapshotIndexEntry: getWorkbenchSnapshotIndexEntry,
      snapshotIndexLoading: workbenchSnapshotIndexLoading,
    });

    if (decision === 'wait') return;

    pendingDeferredWorkbenchStreamRef.current = false;
    pendingDeferredPlanIdRef.current = undefined;

    if (decision === 'skip') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      return;
    }

    if (decision === 'resume-build') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      runResumeInterruptedBuild(planId);
      return;
    }

    onStreamingPrototypeStart?.({ llmInteractionId: planId });
    lastStreamingBuildLlmInteractionIdRef.current = planId;
  }, [
    isStreamingPrototype,
    workbenchSnapshotBootstrap,
    isPrototypeOpen,
    isStreaming,
    onStreamingPrototypeStart,
    getLatestPrototyperWorkbenchPlanMessage,
    currentConversationId,
    getWorkbenchSnapshotIndexEntry,
    workbenchSnapshotIndexLoading,
    runResumeInterruptedBuild,
  ]);

  /**
   * Reset prototype streaming refs when conversation changes (not on mount).
   */
  useEffect(() => {
    const id = currentConversationId ?? null;
    if (lastModuleResetConversationIdRef.current === undefined) {
      lastModuleResetConversationIdRef.current = id;
      return;
    }
    if (lastModuleResetConversationIdRef.current === id) return;
    lastModuleResetConversationIdRef.current = id;
    lastStreamingBuildLlmInteractionIdRef.current = undefined;
    pendingDeferredWorkbenchStreamRef.current = false;
    pendingDeferredPlanIdRef.current = undefined;
    resumeStartedIdsRef.current = new Set();
    setIsPrototypeOpen(false);
    onStreamingWorkbenchPrototypePanelVisibilityChangeRef.current?.(false);
  }, [currentConversationId]);

  /**
   * Handle "Generate Prototype" button click from Uncover mode
   * Sends "Generate a prototype" message with prototyper mode to trigger the normal header flow
   * User stays in Uncover mode menu throughout the process
   */
  const handleGeneratePrototypeFromUncover = useCallback(() => {
    if (!currentConversationId) return;

    // Check for prototype credits before proceeding
    if (!hasPrototypeCredits) {
      // Navigate to billing if no prototype credits
      facade.navigation.toBilling();
      return;
    }

    // Mark that this prototype was initiated from uncover mode
    // This prevents the mode switch in the prototyper header effect
    facade.conversation.setInitiatedFromUncover(true);
    // Temporarily switch to prototyper mode for the API request
    facade.conversation.setMode('prototyper');

    // Reset header states before submitting
    facade.headers.setIsClarification(false);
    facade.headers.setIsReflection(false);
    facade.headers.setIsRepeated(false);
    facade.headers.setIsPrototyper(false);
    facade.headers.setIsReadyToCreate(false);
    lastStreamingBuildLlmInteractionIdRef.current = undefined;

    // Submit "Generate a prototype" message with prototyper mode override
    // The modeOverride ensures the API receives 'prototyper' mode even though user stays in uncover menu
    facade.actions.submitMessage(
      'Generate a prototype',
      [],
      (newConversationId, headers) => {
        // Process headers when streaming completes — but only if the user hasn't since
        // navigated to a different conversation (see isReplyStillActive).
        // The normal header flow will create the prototype when x-prototyper header is received
        if (headers && isReplyStillActive(currentConversationId, newConversationId)) {
          facade.headers.processHeaders(
            headers,
            currentConversationId,
            newConversationId,
            undefined,
            urlConversationId,
            facade.state.conversationId || undefined
          );
        }
      },
      'prototyper'
    );
  }, [currentConversationId, facade, hasPrototypeCredits, isReplyStillActive]);

  /**
   * Handle "Back to Uncover" button click
   * Closes the prototype panel and returns to full-width Uncover chat
   */
  const handleBackToUncover = useCallback(() => {
    facade.conversation.setMode('clarify');
    setIsPrototypeOpen(false);
  }, []);

  /**
   * Handle retry prototyper creation
   */
  const handleRetryPrototyper = useCallback(() => {
    if (!currentConversationId) {
      return;
    }
    facade.prototyper.clearError();
    if (isStreamingPrototype) {
      if (isStreaming) {
        showToast({
          message: 'Wait for the plan to finish streaming…',
          severity: 'info',
          position: 'top-center',
        });
        return;
      }
      if (!isWorkbenchStreamingPlanReady()) {
        showToast({
          message: 'Prototype plan is not ready yet.',
          severity: 'warning',
          position: 'top-center',
        });
        return;
      }
      const planMsg = getLatestPrototyperWorkbenchPlanMessage();
      const id = planMsg?.llmInteractionId;
      if (!id) {
        showToast({
          message: 'Waiting for the prototype plan to be saved…',
          severity: 'info',
          position: 'top-center',
        });
        return;
      }
      onStreamingPrototypeStart?.({ llmInteractionId: id });
      return;
    }
    showToast({
      message: 'Prototype generation is only available in the web workbench.',
      severity: 'warning',
      position: 'top-center',
    });
  }, [
    currentConversationId,
    facade.prototyper,
    isStreaming,
    isStreamingPrototype,
    isWorkbenchStreamingPlanReady,
    getLatestPrototyperWorkbenchPlanMessage,
    onStreamingPrototypeStart,
  ]);

  /**
   * Handle prototyper banner click
   * If prototyper_demo_link_id is provided, fetch the prototyper data
   * Otherwise, just open the panel (already created via header)
   */
  const handlePrototyperClick = useCallback(
    async (prototyperDemoLinkId?: string | number, meta?: { llmInteractionId?: string | null }) => {
      const lid = meta?.llmInteractionId ?? undefined;
      // The panel is auto-closed by the mode-change effect at line ~927
      // whenever `currentMode === 'clarify'`. Clicking "View prototype"
      // needs the mode to flip first so that effect doesn't cancel out
      // the panel we're about to open. Same switch the "Generate
      // Prototype" button uses.
      if (currentMode !== 'prototyper') {
        facade.mode.handleModeChange('prototyper');
      }
      setIsPrototypeOpen(true);

      if (
        lid &&
        onPrototyperWorkbenchRestore &&
        isStreamingPrototype &&
        currentProjectId &&
        currentConversationId
      ) {
        const sourceMessage = [...messages]
          .reverse()
          .find((m) => m.role === 'assistant' && m.llmInteractionId === lid);
        try {
          await onPrototyperWorkbenchRestore(lid, {
            buildStatus: sourceMessage?.buildStatus,
            hasBuildTimeline: Boolean(sourceMessage?.buildTimeline),
          });
        } catch (error) {
          console.error('Failed to restore prototype snapshot:', error);
          if (isSupersededRestoreError(error)) {
            return;
          }
          // A message can carry `hasPrototype: true` (rendering this pill) for a turn
          // whose build never actually ran or hasn't finished yet — the pill signals
          // "this is a prototyper turn," not "a build definitely exists." Surfacing
          // that as a scary red error is misleading; say what's actually true instead.
          if (isBenignMissingSnapshotError(error)) {
            showToast({
              message: "This turn hasn't built a prototype yet.",
              severity: 'info',
              position: 'top-center',
            });
          } else {
            showToast({
              message:
                error instanceof Error
                  ? error.message
                  : 'Could not restore this prototype version. Try again in a moment.',
              severity: 'error',
              position: 'top-center',
            });
          }
        }
      }

      // If we have a demo link ID, check if it's different from current
      if (prototyperDemoLinkId) {
        const currentId = currentPrototypeId;

        // Normalize both IDs to numbers for comparison
        const normalizedCurrentId = currentId
          ? typeof currentId === 'string'
            ? parseInt(currentId, 10)
            : currentId
          : null;
        const normalizedRequestedId =
          typeof prototyperDemoLinkId === 'string'
            ? parseInt(prototyperDemoLinkId, 10)
            : prototyperDemoLinkId;

        // Only fetch if it's a different prototyper or we don't have data yet
        if (normalizedCurrentId !== normalizedRequestedId) {
          try {
            await fetchPrototyperById(prototyperDemoLinkId);
          } catch (error) {
            console.error('Failed to fetch prototyper:', error);
            return;
          }
        }
      }
    },
    [
      fetchPrototyperById,
      currentPrototypeId,
      onPrototyperWorkbenchRestore,
      isStreamingPrototype,
      currentProjectId,
      currentConversationId,
      isWeb,
      currentMode,
      facade.mode,
      messages,
    ]
  );

  return {
    handleGeneratePrototypeFromUncover,
    handleBackToUncover,
    handleRetryPrototyper,
    handlePrototyperClick,
    resetBuildStreamLatch,
  };
}

export type ConversationPrototypeStream = ReturnType<typeof useConversationPrototypeStream>;
